import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Divider,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseSharp";
import ShareIcon from "@mui/icons-material/ShareSharp";

import { useCreateTicket } from "@hooks/use-create/ticket";
import { useUpdateTicket } from "@hooks/use-update/ticket";
import { useTicketConfigContext } from "@hooks/use-config-context/ticket";
import { useTicketTableDispatch } from "@hooks/use-table-context/ticket";
import { useUserContext } from "@hooks/use-auth-context";
import { useCapabilities } from "@hooks/use-capabilities";
import { TableCellInputType } from "@components/data-table/types";
import { initialValues } from "@components/data-table/add-new-modal/types";
import { useFormState } from "@hooks/use-form-state";
import { formatTicketForSave } from "@utils/format-ticket-for-save";
import {
  createFormFields,
  createFormTabs,
  copyModalLinkToClipboard,
  FormTab,
} from "@views/asset-table/helperFunctions";
import { decodeHtmlEntities } from "@utils/index";
import { DynamicForm } from "@components/dynamicForm";
import { useGetTickets } from "@hooks/use-get/tickets";
import { CorrespondenceBox } from "./correspondence-box";
import { CorrespondenceForm } from "./correspondence-form";
import { EmailAlertMsg } from "./email-alert-msg";
import { ToolTipIconButton } from "@components/tool-tip-icon-button";
import { OutlinedSelect } from "@components/outlined-select";
import { FileDisplayList } from "@components/file-display-list";
import { useFileUpload } from "@hooks/use-file-upload";
import { useCheckUnique, getUniquenessHelperText } from "@hooks/use-check-unique";
import { TICKET_KEY_FOR_ASSETS } from "@utils/types";
import {
  fixedModalDialogContentSx,
  fixedModalPaperSx,
  modalHeaderActionsSx,
  modalTabsContainerSx,
  modalTabsSx,
  modalTitleSx,
} from "@utils/modal-styles";

export const AddTicketModal = ({
  open,
  onClose,
  editData,
}: {
  open?: boolean;
  onClose: () => void;
  editData?: any;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openTabId, setOpenTabId] = useState<number>(0);
  const [liveEditData, setLiveEditData] = useState<any>(null);
  const [alertStatus, setAlertStatus] = useState<string>("");
  const [correspondenceVisibility, setCorrespondenceVisibility] =
    useState<string>("internal");
  const [linkAlertOpen, setLinkAlertOpen] = useState<boolean>(false);
  const [alertMsg, setAlertMsg] = useState<string>("");
  const [noteVisFilter, setNoteVisFilter] = useState<string>("all");

  const { createTicket } = useCreateTicket();
  const { updateTicket } = useUpdateTicket();
  const { tableColumns, config: ticketsConfig, loading: configLoading } = useTicketConfigContext();
  const { refreshItems } = useTicketTableDispatch();
  const { checkUserAuthentication } = useUserContext();
  const { can } = useCapabilities();
  const canEdit = can("edit_tickets");
  const { getOneTicket } = useGetTickets();
  const { files, addFiles, removeFile, clearFiles, uploadFiles } = useFileUpload();

  const resolveTicketTitle = (source: any): string => {
    if (!source) return "";

    const title = source.title;
    if (typeof title === "string") {
      return decodeHtmlEntities(title);
    }

    if (title && typeof title === "object" && typeof title.rendered === "string") {
      return decodeHtmlEntities(title.rendered);
    }

    return "";
  };

  const pendingTicketFileIdsRef = useRef<number[]>([]);

  const mergeTicketWithSavedPayload = useCallback((ticket: any, payload: Record<string, any>) => {
    if (!ticket) return ticket;

    return {
      ...ticket,
      title: payload.title
        ? {
          ...(typeof ticket.title === "object" && ticket.title ? ticket.title : {}),
          rendered: payload.title,
        }
        : ticket.title,
      meta_box: {
        ...(ticket.meta_box ?? {}),
        ...(payload.meta_box ?? {}),
      },
    };
  }, []);

  useEffect(() => {
    if (editData) {
      getOneTicket(editData.id).then((res) => {
        setLiveEditData(res);
      });
    }
  }, [editData, getOneTicket]);

  useEffect(() => {
    if (alertStatus !== "") {
      const timer = setTimeout(() => {
        setAlertStatus("");
      }, 15000); // Alert will disappear after 15 seconds

      return () => clearTimeout(timer); // Clear timeout if component unmounts or alertStatus changes
    }
  }, [alertStatus]);

  const refreshCorrespondenceList = async () => {
    if (editData) {
      const updatedTicket = await getOneTicket(editData.id);
      setLiveEditData(updatedTicket);
    }
  };

  const ticketTitleField = useMemo(
    () => ({
      id: "title",
      name: "title",
      label: "Ticket #",
      inputType: TableCellInputType.Text,
      required: false,
      options: [],
      taxonomyOptions: [],
      subFields: [],
    }),
    [],
  );

  const formTabs: FormTab[] = useMemo(() => {
    const tabs = createFormTabs(
      tableColumns,
      ticketsConfig,
      "ticket",
      "Ticket",
    );

    if (tabs[0]?.fields && editData) {
      tabs[0].fields["title"] = ticketTitleField;
    }

    tabs.push({
      id: tabs.length,
      name: "notes",
      label: "Notes",
      fields: {},
    });

    return tabs;
  }, [tableColumns, ticketsConfig, ticketTitleField, editData]);

  const formFields: Record<string, any> = useMemo(() => {
    const fields = createFormFields(tableColumns, ticketsConfig);
    fields.title = ticketTitleField;
    return fields;
  }, [tableColumns, ticketsConfig, ticketTitleField]);

  const normalizeFieldValue = useCallback((column: any, rawValue: any) => {
    if (rawValue === undefined || rawValue === null) {
      return initialValues[column.inputType as TableCellInputType] ?? "";
    }

    if (Array.isArray(rawValue)) {
      if (column.id === "files_group") {
        return rawValue;
      }

      const firstValue = rawValue[0];

      if (column.id === TICKET_KEY_FOR_ASSETS) {
        if (firstValue && typeof firstValue === "object") {
          return firstValue.id ? String(firstValue.id) : "";
        }
        return firstValue ?? "";
      }

      return firstValue ?? "";
    }

    if (typeof rawValue === "string") {
      return decodeHtmlEntities(rawValue);
    }

    return rawValue;
  }, []);

  const readRawFieldValue = useCallback((column: any, source: any) => {
    if (!source) return undefined;

    if (column.id === "title") {
      return resolveTicketTitle(source);
    }

    if (source?.meta_box && source.meta_box[column.id] !== undefined) {
      return source.meta_box[column.id];
    }

    if (source[column.id] !== undefined) {
      return source[column.id];
    }

    return undefined;
  }, []);

  const buildFormState = useCallback((primarySource: any, fallbackSource: any) => {
    return Object.values(formFields).reduce((acc, column: any) => {
      const primaryValue = readRawFieldValue(column, primarySource);
      const fallbackValue = readRawFieldValue(column, fallbackSource);

      const chosenValue =
        primaryValue !== undefined && primaryValue !== null
          ? primaryValue
          : fallbackValue;

      acc[column.id] = normalizeFieldValue(column, chosenValue);
      return acc;
    }, {} as Record<string, any>);
  }, [formFields, normalizeFieldValue, readRawFieldValue]);

  const initialState = useMemo(
    () => buildFormState(liveEditData, editData),
    [buildFormState, liveEditData, editData],
  );

  const getOrUploadTicketFileIds = async (): Promise<number[]> => {
    if (pendingTicketFileIdsRef.current.length > 0) {
      return pendingTicketFileIdsRef.current;
    }

    if (!files || files.length === 0) {
      return [];
    }

    const uploadResults = await uploadFiles(files);
    const ids = uploadResults.map((res) => res.id);

    pendingTicketFileIdsRef.current = ids;
    return ids;
  };

  const { state, setState, handleChange, handleSubmit, handleReset } =
    useFormState({
      initialState,
      onSubmit: async (state) => {
        await checkUserAuthentication();
        // Handle form submission logic here
        setIsLoading(true);

        const payloadState = { ...state };

        // Handle file uploads and get their IDs.
        // Source truth is selected File[] state, not files_group form field shape.
        const wpFileIds = await getOrUploadTicketFileIds();
        const wpFileIdsAsStrings = wpFileIds.map(String);
        const existingIds: string[] = liveEditData?.meta_box?.files_group?.[0]?.map(String) || [];

        if (wpFileIdsAsStrings.length > 0) {
          payloadState.files_group = [[
            ...existingIds,
            ...wpFileIdsAsStrings,
          ]];
          clearFiles();
          pendingTicketFileIdsRef.current = [];
        } else if (existingIds.length > 0) {
          // Preserve existing attachments when no new files are selected.
          payloadState.files_group = [existingIds];
        }

        if (editData?.id) {
          const formattedPayload = formatTicketForSave(payloadState, ticketsConfig);

          const updatedTicket = await updateTicket(
            editData.id,
            formattedPayload,
          );

          if (updatedTicket) {
            setLiveEditData(mergeTicketWithSavedPayload(updatedTicket, formattedPayload));
          }
        } else {
          await createTicket(formatTicketForSave(payloadState, ticketsConfig));
        }

        await refreshItems();
        setIsLoading(false);
        !editData && handleReset();

        /*
          If new Ticket close modal after submission.
          If editing an existing Ticket, leave open for user to see changes.
        */
        !editData && onClose();
      },
    });

  const isConfigReady: boolean = !configLoading && tableColumns.length > 0;
  const hasEditData: boolean = !!editData;

  // Uniqueness check for ticket number (post_title).
  // Use "" when value is unchanged from the original so the hook skips silently.
  const originalTicketTitle = resolveTicketTitle(liveEditData ?? editData);
  const effectiveTicketTitleValue = String(state.title ?? "") === originalTicketTitle ? "" : String(state.title ?? "");

  const ticketNumberCheck = useCheckUnique({
    type: "ticket",
    source: "post_title",
    value: effectiveTicketTitleValue,
    excludeId: editData?.id,
  });
  const fieldHelperTexts = {
    title: getUniquenessHelperText(ticketNumberCheck.isChecking, ticketNumberCheck.isUnique, effectiveTicketTitleValue),
  };

  useEffect(() => {
    if (!hasEditData) return;
    if (!isConfigReady) return;

    setState(buildFormState(liveEditData, editData));
  }, [hasEditData, isConfigReady, editData, liveEditData, setState, buildFormState]);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: fixedModalPaperSx }}
    >
      {open ? (
        <>
          <DialogTitle sx={modalTitleSx}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1}
            >
              <Typography variant="h6" gutterBottom>
                {editData
                  ? canEdit ? "Edit Ticket" : "View Ticket"
                  : "Add New Ticket"}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={modalHeaderActionsSx}
              >
                {liveEditData && can("read_ticket") && (
                  <ToolTipIconButton
                    text="Share Ticket"
                    icon={<ShareIcon />}
                    onClick={() => copyModalLinkToClipboard(
                      liveEditData.slug,
                      "tickets",
                      setLinkAlertOpen,
                      setAlertMsg
                    )}
                  />
                )}
                <ToolTipIconButton
                  text="Close Ticket"
                  icon={<CloseIcon />}
                  onClick={onClose}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={{ xs: 1, sm: 2 }} mb={editData ? 1 : 0}>
              {editData && (
                <Box
                  sx={modalTabsContainerSx}
                >
                  <Tabs
                    value={openTabId}
                    textColor="primary"
                    indicatorColor="primary"
                    aria-label="ticket tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={modalTabsSx}
                  >
                    {formTabs.map((tab: FormTab) => (
                      <Tab
                        key={tab.name}
                        label={tab.label}
                        value={tab.id}
                        aria-label={tab.label}
                        onClick={() => setOpenTabId(tab.id)}
                      />
                    ))}
                  </Tabs>
                </Box>
              )}
            </Stack>

            {/* Email Status Alert */}
            {alertStatus !== "" && correspondenceVisibility === "public" && (
              <EmailAlertMsg
                status={alertStatus}
                closeAlert={() => setAlertStatus("")}
              />
            )}

            {/* Modal Link Alert */}
            {linkAlertOpen && (
              <Alert
                severity="info"
                sx={{ mb: 1 }}
              >
                {alertMsg}
              </Alert>
            )}
          </DialogTitle>

          <DialogContent
            dividers
            sx={fixedModalDialogContentSx}
          >
          {isLoading && (
            <Box
              sx={{
                flex: 1,
                minHeight: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {!isLoading && formTabs[openTabId].name !== "notes" && (
            <DynamicForm
              formFields={formTabs[openTabId].fields}
              state={state}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              handleReset={handleReset}
              files={files}
              addFiles={addFiles}
              removeFile={removeFile}
              fieldHelperTexts={fieldHelperTexts}
              disabledFields={editData ? { title: true } : undefined}
              readOnly={!!editData && !canEdit}
            >
              {/* Files tab — display existing files below the upload field */}
              {liveEditData && formTabs[openTabId].name === "files_group" && (
                <>
                  <Divider sx={{ my: 2 }} />

                  {liveEditData?.meta_box?.files_group?.[0]?.length === 0
                    ? (
                      <Typography variant="body2">
                        No files uploaded
                      </Typography>
                    ) : (
                      <FileDisplayList
                        fileIds={liveEditData?.meta_box?.files_group?.[0]}
                      />
                    )
                  }
                </>
              )}
            </DynamicForm>
          )}

          {/* Notes tab — no DynamicForm, scroll body only (no fixed footer) */}
          {!isLoading && liveEditData && formTabs[openTabId].name === "notes" && (
            <div className="fixed-modal__body">
              <CorrespondenceForm
                ticketId={liveEditData.id}
                refreshCorrespondenceList={refreshCorrespondenceList}
                setVisibility={(value: string) =>
                  setCorrespondenceVisibility(value)
                }
                setAlertStatus={(val: string) => setAlertStatus(val)}
              />

              {liveEditData.correspondence_notes && (
                <>
                  <Divider sx={{ my: 2 }} />

                  {(() => {
                    const filteredNotes: Record<string, any>[] = noteVisFilter === "all"
                      ? liveEditData.correspondence_notes
                      : liveEditData.correspondence_notes.filter(
                        (note: Record<string, any>) => note.visibility === noteVisFilter
                      );

                    return (
                      <Box>
                        <OutlinedSelect
                          label="Notes Visibility Filter"
                          value={noteVisFilter}
                          onChange={(e: string) => setNoteVisFilter(e)}
                          options={[
                            { label: "All", value: "all" },
                            { label: "Internal", value: "internal" },
                            { label: "Public", value: "public" },
                          ]}
                        />

                        {filteredNotes.length === 0
                          ? (
                            <Typography variant="body1" my={3} textAlign="center">
                              No Notes to Display
                            </Typography>
                          )
                          : (
                            <Box
                              sx={{
                                maxHeight: { xs: 260, sm: 340 },
                                overflowY: "auto",
                                mt: 1,
                                pr: 2,
                                pt: 1,
                              }}
                            >
                              {filteredNotes.map(
                                (note: Record<string, any>) => (
                                  <CorrespondenceBox
                                    key={note.id}
                                    sender_email={note.sender_email}
                                    note_text={note.note_text}
                                    body_html={note.body_html}
                                    date={note.created_at}
                                    visibility={note.visibility}
                                  />
                                )
                              )}
                            </Box>
                          )
                        }
                      </Box>
                    )
                  })()}
                </>
              )}
            </div>
          )}
          </DialogContent>
        </>
      ) : (
        <></>
      )}
    </Dialog>
  );
};
